import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import {
  DemographicRow,
  DemographicSeparator,
  getPatientDetailTabIcon,
  AddVitalIcon,
  HeaderAddNoteIcon,
  HeaderEditIcon,
  NoteDeleteIcon,
  NoteEditIcon,
  UploadReportIcon,
} from "./icons/patientDetailIcons";
import { formatNoteDateTime } from "../utils/formatNoteDateTime";
import Button from "./ButtonComponent";
import ChatWidget from "./ChatWidget";
import HealthMetricsDashboard from "./HealthMetricsDashboard";
import MedicalHistoryTab from "./MedicalHistoryTab";
import ReportsTab from "./ReportsTab";
import PatientAiOverviewPanel from "./PatientAiOverviewPanel";
import PatientDetailModal from "./PatientDetailModal";
import PatientForm from "./PatientForm";
import QuestionnaireTab from "./QuestionnaireTab";
import { PageLoading } from "./loading/LoadingIndicators";
import type { PatientFormPayload } from "./PatientForm";
import { useState, useEffect, useRef, useCallback } from "react";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { API_BASE_URLS, PATIENT_DETAILS, getSuperEcgWhatsappEndpoint } from "../constants/api";
import { CSS_CLASSES, MESSAGES } from "../constants/app";
import type { Patient, Report } from "../types";
import { apiUtils, auth, riskUtils } from "../utils";
import { getPatientMobileValidationError } from "../utils/validatePatientMobile";

const PATIENT_DETAIL_TABS = [
  "Health Metrics",
  "Medical History",
  "Questionnaire",
  "Reports",
  "Notes",
  "Chat",
] as const;

const SCROLLABLE_LIST_TABS = new Set<string>([
  "Questionnaire",
  "Reports",
  "Notes",
]);

// Validation ranges for vitals
const HEART_RATE_MIN = 30;
const HEART_RATE_MAX = 250;
const BP_SYSTOLIC_MIN = 70;
const BP_SYSTOLIC_MAX = 250;
const BP_DIASTOLIC_MIN = 40;
const BP_DIASTOLIC_MAX = 150;
const OXYGEN_MIN = 70;
const OXYGEN_MAX = 100;

const CLINICIAN_REPORT_TYPES: { value: string; label: string }[] = [
  { value: "chart", label: "Chart" },
  { value: "ecg", label: "ECG" },
  { value: "crp", label: "CRP" },
  { value: "cbc", label: "CBC" },
  { value: "lipid_profile", label: "Lipid profile" },
  { value: "trop_i", label: "Troponin I" },
];

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="currentColor"
  >
    <path d="M12.04 2C6.58 2 2.13 6.36 2.13 11.72c0 1.71.46 3.39 1.34 4.86L2 22l5.58-1.43a10.13 10.13 0 0 0 4.46 1.04h.01c5.46 0 9.9-4.36 9.9-9.72C21.95 6.36 17.5 2 12.04 2Zm0 17.95h-.01a8.38 8.38 0 0 1-4.25-1.16l-.31-.18-3.31.85.88-3.16-.2-.32a7.86 7.86 0 0 1-1.22-4.26c0-4.45 3.78-8.06 8.43-8.06 2.25 0 4.37.86 5.96 2.37a7.84 7.84 0 0 1 2.48 5.86c0 4.45-3.78 8.06-8.44 8.06Zm4.63-6.04c-.25-.12-1.49-.72-1.72-.8-.23-.09-.4-.12-.57.12-.17.25-.65.8-.8.96-.15.17-.29.18-.54.06-.25-.12-1.06-.38-2.02-1.2-.75-.66-1.25-1.47-1.4-1.72-.15-.25-.02-.38.11-.5.12-.11.25-.29.38-.43.12-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.57-1.34-.78-1.84-.2-.48-.41-.42-.57-.43h-.49c-.17 0-.43.06-.66.31-.23.25-.87.84-.87 2.04s.89 2.37 1.01 2.53c.12.17 1.75 2.61 4.25 3.66.59.25 1.06.4 1.42.51.6.19 1.14.16 1.57.1.48-.07 1.49-.59 1.7-1.17.21-.58.21-1.08.15-1.18-.06-.1-.23-.16-.48-.28Z" />
  </svg>
);

export default function PatientDetails() {
  // Tab state and tabs array must be at the top
  const [selectedTab, setSelectedTab] = useState("Health Metrics");
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [isAiOverviewExpanded, setIsAiOverviewExpanded] = useState(true);

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [notes, setNotes] = useState<
    { text: string; date: Date; id?: string; updatedAt?: Date }[]
  >([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);
  useEffect(() => {
    const state = location.state as { openChat?: boolean } | null;
    if (state?.openChat) {
      setSelectedTab("Chat");
      setIsAiOverviewExpanded(false);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const [reports, setReports] = useState<Record<string, Report[]> | null>(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportActionError, setReportActionError] = useState<string | null>(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfViewerError, setPdfViewerError] = useState<string | null>(null);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [showShareReportModal, setShowShareReportModal] = useState(false);
  const [reopenShareReportModalAfterEdit, setReopenShareReportModalAfterEdit] =
    useState(false);
  const reportFetchGenerationRef = useRef(0);
  const [lastReportFetchParams, setLastReportFetchParams] = useState<{
    reportType: string;
    fileName: string;
    variant?: "original" | "derivative";
    fallbackFileName?: string;
  } | null>(null);
  const [reportAnalysis, setReportAnalysis] = useState<any>({});
  const [reportSortOrder, setReportSortOrder] = useState<"newest" | "oldest">(
    "newest"
  );
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);

  const [showUploadReportModal, setShowUploadReportModal] = useState(false);
  const [uploadReportType, setUploadReportType] = useState("");
  const [uploadReportFile, setUploadReportFile] = useState<File | null>(null);
  const [uploadReportError, setUploadReportError] = useState<string | null>(
    null
  );
  const [uploadReportSubmitting, setUploadReportSubmitting] = useState(false);

  // Add Vital modal state
  const [showAddVitalModal, setShowAddVitalModal] = useState(false);
  const [addVitalHeartRate, setAddVitalHeartRate] = useState("");
  const [addVitalSystolic, setAddVitalSystolic] = useState("");
  const [addVitalDiastolic, setAddVitalDiastolic] = useState("");
  const [addVitalOxygen, setAddVitalOxygen] = useState("");
  const [addVitalErrors, setAddVitalErrors] = useState<Record<string, string>>({});
  const [addVitalSubmitting, setAddVitalSubmitting] = useState(false);
  const [vitalConfig, setVitalConfig] = useState<{
    _id: string;
    vitals: { _id: string; vitalId: string; vitalName: string }[];
  } | null>(null);

  // Health Metrics Dashboard state
  const [heartRateDataForChart, setHeartRateDataForChart] = useState<number[]>(
    []
  );
  const [bloodPressureDataForChart, setBloodPressureDataForChart] = useState<
    [number, number][]
  >([]);
  const [bloodOxygenDataForChart, setBloodOxygenDataForChart] = useState<
    number[]
  >([]);
  const [heartRateTimeLabels, setHeartRateTimeLabels] = useState<string[]>([]);
  const [bloodPressureTimeLabels, setBloodPressureTimeLabels] = useState<
    string[]
  >([]);
  const [bloodOxygenTimeLabels, setBloodOxygenTimeLabels] = useState<string[]>(
    []
  );
  const [latestHeartRate, setLatestHeartRate] = useState<number | null>(null);
  const [latestBloodPressure, setLatestBloodPressure] = useState<{
    systolic: number;
    diastolic: number;
  } | null>(null);
  const [latestBloodOxygen, setLatestBloodOxygen] = useState<number | null>(
    null
  );
  const [latestHeartRateTime, setLatestHeartRateTime] = useState("");
  const [latestBloodPressureTime, setLatestBloodPressureTime] = useState("");
  const [latestBloodOxygenTime, setLatestBloodOxygenTime] = useState("");

  const refetchPatient = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("GET");
      const response = await fetch(`${PATIENT_DETAILS}/${id}`, requestOptions);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch patient details");
      }
      const result = await response.json();
      setPatient(result);
    } catch (err: any) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refetchPatient();
  }, [refetchPatient]);

  // Fetch vital config when Add Vital modal opens
  useEffect(() => {
    if (!showAddVitalModal) return;
    let cancelled = false;
    const fetchConfig = async () => {
      try {
        const requestOptions = apiUtils.createRequestOptions("GET");
        const response = await fetch(
          `${API_BASE_URLS.HELIUS_SERVICE}/config`,
          requestOptions
        );
        if (response.ok && !cancelled) {
          const data = await response.json();
          if (data.healthVitals) {
            setVitalConfig({
              _id: data.healthVitals._id,
              vitals: data.healthVitals.vitals || [],
            });
          }
        }
      } catch {
        if (!cancelled) setVitalConfig(null);
      }
    };
    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, [showAddVitalModal]);

  const validateAddVitalForm = (): boolean => {
    const err: Record<string, string> = {};
    const hr = addVitalHeartRate.trim();
    const sys = addVitalSystolic.trim();
    const dia = addVitalDiastolic.trim();
    const ox = addVitalOxygen.trim();

    if (!hr && !sys && !dia && !ox) {
      err.form = "Enter at least one vital (Heart Rate, Blood Pressure, or Oxygen Saturation).";
      setAddVitalErrors(err);
      return false;
    }

    if (hr) {
      const n = Number(hr);
      if (Number.isNaN(n) || !Number.isInteger(n) || n < HEART_RATE_MIN || n > HEART_RATE_MAX) {
        err.heartRate = `Heart rate must be between ${HEART_RATE_MIN} and ${HEART_RATE_MAX} bpm.`;
      }
    }
    if (sys || dia) {
      const s = Number(sys);
      const d = Number(dia);
      if (sys && (Number.isNaN(s) || !Number.isInteger(s) || s < BP_SYSTOLIC_MIN || s > BP_SYSTOLIC_MAX)) {
        err.systolic = `Systolic must be between ${BP_SYSTOLIC_MIN} and ${BP_SYSTOLIC_MAX} mmHg.`;
      }
      if (dia && (Number.isNaN(d) || !Number.isInteger(d) || d < BP_DIASTOLIC_MIN || d > BP_DIASTOLIC_MAX)) {
        err.diastolic = `Diastolic must be between ${BP_DIASTOLIC_MIN} and ${BP_DIASTOLIC_MAX} mmHg.`;
      }
      if (sys && dia && !err.systolic && !err.diastolic && s <= d) {
        err.bloodPressure = "Systolic must be greater than diastolic.";
      }
    }
    if (ox) {
      const o = Number(ox);
      if (Number.isNaN(o) || o < OXYGEN_MIN || o > OXYGEN_MAX) {
        err.oxygen = `Oxygen saturation must be between ${OXYGEN_MIN} and ${OXYGEN_MAX}%.`;
      }
    }

    setAddVitalErrors(err);
    return Object.keys(err).length === 0;
  };

  const resetAddVitalForm = () => {
    setAddVitalHeartRate("");
    setAddVitalSystolic("");
    setAddVitalDiastolic("");
    setAddVitalOxygen("");
    setAddVitalErrors({});
  };

  const handleAddVitalSubmit = async () => {
    if (!patient?.userId || !vitalConfig) return;
    if (!validateAddVitalForm()) return;

    setAddVitalSubmitting(true);
    setAddVitalErrors({});

    const now = new Date().toISOString();
    const vitals: Array<{
      moduleId: string;
      bodyType: string;
      body: Record<string, number>;
      startDate: string;
      endDate: string;
    }> = [];

    const getModuleId = (vitalId: string) =>
      vitalConfig.vitals.find((v) => v.vitalId === vitalId)?._id;

    const hr = addVitalHeartRate.trim();
    const sys = addVitalSystolic.trim();
    const dia = addVitalDiastolic.trim();
    const ox = addVitalOxygen.trim();

    if (hr) {
      const moduleId = getModuleId("heart_rate");
      if (moduleId) {
        vitals.push({
          moduleId,
          bodyType: "HEART_RATE",
          body: { value: Number(hr) },
          startDate: now,
          endDate: now,
        });
      }
    }
    if (sys && dia) {
      const moduleId = getModuleId("blood_pressure");
      if (moduleId) {
        vitals.push({
          moduleId,
          bodyType: "BLOOD_PRESSURE",
          body: { systolic: Number(sys), diastolic: Number(dia) },
          startDate: now,
          endDate: now,
        });
      }
    }
    if (ox) {
      const moduleId = getModuleId("blood_oxygen");
      if (moduleId) {
        vitals.push({
          moduleId,
          bodyType: "BLOOD_OXYGEN",
          body: { value: Number(ox) },
          startDate: now,
          endDate: now,
        });
      }
    }

    if (vitals.length === 0) {
      setAddVitalErrors({ form: "Could not resolve vital config. Please try again." });
      setAddVitalSubmitting(false);
      return;
    }

    try {
      const requestOptions = apiUtils.createRequestOptions("POST", { vitals });
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/patients/${patient.userId}/vitals/${vitalConfig._id}`,
        requestOptions
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setAddVitalErrors({ form: data.error || "Failed to save vitals." });
        setAddVitalSubmitting(false);
        return;
      }

      setShowAddVitalModal(false);
      resetAddVitalForm();
      refetchPatient();
    } catch (err: any) {
      setAddVitalErrors({ form: err.message || "Failed to save vitals." });
    } finally {
      setAddVitalSubmitting(false);
    }
  };

  const refetchReports = useCallback(async () => {
    if (!patient?.userId) return;

    setReportsLoading(true);
    setReportsError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("GET");
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/reports/${patient.userId}`,
        requestOptions
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch reports");
      }
      const result = await response.json();
      setReports((result.reports || {}) as Record<string, Report[]>);
      setReportActionError(null);

      const analysisData: Record<string, any> = {};
      for (const [reportType, reportList] of Object.entries(result.reports)) {
        if (Array.isArray(reportList) && reportList.length > 0) {
          for (const report of reportList) {
            if (report.data) {
              analysisData[`${reportType}_${report._id}`] = {
                ...report.data,
                createdAt: report.createdAt,
              };
            }
          }
        }
      }
      setReportAnalysis(analysisData);
    } catch (err: any) {
      setReportsError(err.message || "Failed to fetch reports");
    } finally {
      setReportsLoading(false);
    }
  }, [patient?.userId]);

  useEffect(() => {
    refetchReports();
  }, [refetchReports]);

  const resetUploadReportForm = () => {
    setUploadReportType("");
    setUploadReportFile(null);
    setUploadReportError(null);
  };

  const handleUploadReportSubmit = async () => {
    if (!id || !patient?.userId) return;
    setUploadReportError(null);
    if (!uploadReportType) {
      setUploadReportError("Please select a report type.");
      return;
    }
    if (!uploadReportFile) {
      setUploadReportError("Please choose a file to upload.");
      return;
    }

    setUploadReportSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadReportFile);
      formData.append("reportType", uploadReportType);

      const headers: HeadersInit = {};
      const token = auth.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/patients/${id}/reports`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: formData,
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          typeof data.error === "string"
            ? data.error
            : "Failed to upload report"
        );
      }

      setShowUploadReportModal(false);
      resetUploadReportForm();
      await refetchReports();
    } catch (err: any) {
      setUploadReportError(err.message || "Failed to upload report");
    } finally {
      setUploadReportSubmitting(false);
    }
  };

  const applySummaryHtml = useCallback((rawText: string) => {
    let htmlContent = rawText;
    if (rawText.includes("```html")) {
      const htmlMatch = rawText.match(/```html\s*([\s\S]*?)\s*```/);
      if (htmlMatch) {
        htmlContent = htmlMatch[1].trim();
      }
    } else if (rawText.includes("```")) {
      const codeMatch = rawText.match(/```\s*([\s\S]*?)\s*```/);
      if (codeMatch) {
        htmlContent = codeMatch[1].trim();
      }
    }
    setAiSummary(htmlContent);
  }, []);

  const fetchAISummary = useCallback(async () => {
    if (!id) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/get-user-medical-summary`,
        apiUtils.createRequestOptions("POST", { patientId: id })
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          typeof data.error === "string"
            ? data.error
            : "Summary request failed"
        );
      }
      applySummaryHtml(data?.data || "No summary available.");
    } catch (err) {
      setAiError("Failed to fetch AI summary.");
    } finally {
      setAiLoading(false);
    }
  }, [applySummaryHtml, id]);

  useEffect(() => {
    if (!patient || reportsLoading || !reports || !id) return;
    void fetchAISummary();
  }, [fetchAISummary, patient, reportsLoading, reports, id]);

  useEffect(() => {
    if (!patient?.userId) return;

    const notesQuery = query(
      collection(db, "patients", patient.userId, "notes"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(notesQuery, (snapshot) => {
      const loadedNotes = snapshot.docs.map((doc) => ({
        id: doc.id,
        text: doc.data().text,
        date: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || undefined,
      }));
      setNotes(loadedNotes);
    });

    return () => unsubscribe();
  }, [patient?.userId]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSortDropdown(false);
      }
    };

    if (showSortDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSortDropdown]);

  useEffect(() => {
    if (window.matchMedia("(min-width: 1024px)").matches) return;
    const activeTab = tabButtonRefs.current[selectedTab];
    if (!activeTab) return;
    activeTab.scrollIntoView({
      inline: "center",
      block: "nearest",
      behavior: "smooth",
    });
  }, [selectedTab]);

  if (loading) {
    return (
      <PageLoading
        message={MESSAGES.LOADING.PATIENT_DETAILS}
        fullScreen
      />
    );
  }

  if (error || !patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Patient Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The patient you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/")}>Back to Home</Button>
        </div>
      </div>
    );
  }

  const riskDisplay = riskUtils.getRiskDisplay(
    patient.score_summary?.risk_level || ""
  );

  // Patient details from new structure
  const fullName = patient.personal_information?.full_name || "-";
  const genderRaw = patient.personal_information?.gender || "-";
  const gender =
    genderRaw.charAt(0).toUpperCase() + genderRaw.slice(1).toLowerCase();
  const dob = patient.personal_information?.dob || "-";
  const phoneRaw = patient.contact_information?.phone || "-";
  const phone = `+91 ${phoneRaw}`;
  const height = patient.physical_details?.height
    ? `${patient.physical_details.height} cm`
    : "-";
  const bloodGroup = patient.physical_details?.blood_group || "A+";
  // Calculate age from dob
  const getAge = (dob: string) => {
    if (!dob || dob === "-") return "-";

    let day: number, month: number, year: number;

    // Check if it's yyyy-mm-dd format
    if (dob.includes("-")) {
      const parts = dob.split("-");
      if (parts.length === 3) {
        year = parseInt(parts[0]);
        month = parseInt(parts[1]);
        day = parseInt(parts[2]);
      } else {
        return "-";
      }
    } else {
      // Handle dd/mm/yyyy format
      const parts = dob.split("/");
      if (parts.length === 3) {
        day = parseInt(parts[0]);
        month = parseInt(parts[1]);
        year = parseInt(parts[2]);
      } else {
        return "-";
      }
    }

    if (!year) return "-";
    const today = new Date();
    let age = today.getFullYear() - year;
    if (
      today.getMonth() + 1 < month ||
      (today.getMonth() + 1 === month && today.getDate() < day)
    ) {
      age--;
    }
    return age;
  };
  const age = getAge(dob);

  const handleEditPatient = async (payload: PatientFormPayload) => {
    if (!id || !patient?.userId) return;
    const response = await fetch(`${PATIENT_DETAILS}/${id}`, {
      ...apiUtils.createRequestOptions("PUT", payload),
    });
    await apiUtils.handleResponse(response);
    await refetchPatient();
    setShowEditPatientModal(false);
    if (reopenShareReportModalAfterEdit) {
      setReopenShareReportModalAfterEdit(false);
      setShowShareReportModal(true);
    }
  };

  const handleCloseEditPatientModal = () => {
    setShowEditPatientModal(false);
    setReopenShareReportModalAfterEdit(false);
  };

  const handleEditPatientFromShareModal = () => {
    setReopenShareReportModalAfterEdit(true);
    setShowShareReportModal(false);
    setShowEditPatientModal(true);
  };

  // Save note to Firebase (handles both create and update)
  const handleSaveNote = async () => {
    const noteText = editingNoteId ? editingNoteText : newNote;
    if (!noteText.trim() || !patient?.userId) return;

    try {
      if (editingNoteId) {
        // Update existing note
        const noteRef = doc(
          db,
          "patients",
          patient.userId,
          "notes",
          editingNoteId
        );
        await updateDoc(noteRef, {
          text: noteText.trim(),
          updatedAt: serverTimestamp(),
        });
        setEditingNoteId(null);
        setEditingNoteText("");
      } else {
        // Create new note
        await addDoc(collection(db, "patients", patient.userId, "notes"), {
          text: noteText.trim(),
          createdAt: serverTimestamp(),
          doctorId: auth.getUserId() || "clinician",
        });
        setNewNote("");
      }

      setShowNoteModal(false);
    } catch (error) {
      console.error("Error saving note:", error);
      // You might want to show an error message to the user
    }
  };

  // Handle edit note
  const handleEditNote = (noteId: string, noteText: string) => {
    setEditingNoteId(noteId);
    setEditingNoteText(noteText);
    setShowNoteModal(true);
  };

  // Handle delete note (show confirmation)
  const handleDeleteNote = (noteId: string) => {
    setNoteToDelete(noteId);
  };

  // Confirm and delete note
  const confirmDeleteNote = async () => {
    if (!noteToDelete || !patient?.userId) return;

    try {
      const noteRef = doc(
        db,
        "patients",
        patient.userId,
        "notes",
        noteToDelete
      );
      await deleteDoc(noteRef);
      setNoteToDelete(null);
    } catch (error) {
      console.error("Error deleting note:", error);
      // You might want to show an error message to the user
    }
  };

  type ReportVariant = "original" | "derivative";
  type ReportFetchMode = "view" | "download";

  const parseContentDispositionFileName = (disposition: string | null): string | null => {
    if (!disposition) return null;
    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1].replace(/['"]/g, ""));
    const plainMatch = disposition.match(/filename="?([^"]+)"?/i);
    if (plainMatch?.[1]) return plainMatch[1];
    return null;
  };

  const fetchReportVariant = async (params: {
    reportType: string;
    fileName: string;
    variant?: ReportVariant;
    mode?: ReportFetchMode;
    fallbackFileName?: string;
  }) => {
    if (!patient?.userId) return;
    const {
      reportType,
      fileName,
      variant = "original",
      mode = "view",
      fallbackFileName,
    } = params;

    const isViewFetchStale = (generation: number) =>
      generation !== reportFetchGenerationRef.current;

    if (mode === "download") {
      setReportActionError(null);
    }

    let viewGeneration = reportFetchGenerationRef.current;
    if (mode === "view") {
      viewGeneration = ++reportFetchGenerationRef.current;
      setLastReportFetchParams({
        reportType,
        fileName,
        variant,
        fallbackFileName,
      });
      setPdfViewerError(null);
      setPdfLoading(true);
      setShowPdfModal(true);
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    }

    try {
      const requestOptions = apiUtils.createRequestOptions("POST", {
        reportType,
        fileName,
        variant,
      });
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/get-report/${patient.userId}`,
        requestOptions
      );

      if (!response.ok) {
        if (variant === "derivative" && response.status === 404) {
          throw new Error("SuperECG file not available for this report.");
        }
        throw new Error("Failed to fetch report");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition");
      const serverFileName = parseContentDispositionFileName(contentDisposition);

      if (mode === "download") {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = serverFileName || fallbackFileName || fileName || "report.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
      }

      if (isViewFetchStale(viewGeneration)) return;

      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfViewerError(null);
    } catch (error) {
      console.error("Error fetching report:", error);
      const message =
        error instanceof Error ? error.message : "Failed to fetch report";
      if (mode === "view") {
        if (!isViewFetchStale(viewGeneration)) {
          setPdfViewerError(message);
        }
      } else {
        setReportActionError(message);
      }
    } finally {
      if (mode === "view" && !isViewFetchStale(viewGeneration)) {
        setPdfLoading(false);
      }
    }
  };

  // Handle report click to fetch and display original PDF
  const handleReportClick = async (reportType: string, fileName: string) => {
    await fetchReportVariant({ reportType, fileName, variant: "original", mode: "view" });
  };

  const canShareChartReportOnWhatsApp =
    showPdfModal &&
    lastReportFetchParams?.reportType === "chart" &&
    (lastReportFetchParams.variant === "original" ||
      lastReportFetchParams.variant === "derivative") &&
    Boolean(lastReportFetchParams.fileName);

  const patientPhoneRaw = patient?.contact_information?.phone ?? "";
  const patientPhoneDisplay = patientPhoneRaw
    ? `+91 ${patientPhoneRaw}`
    : "+91 —";
  const shareReportMobileError = getPatientMobileValidationError(
    patient?.contact_information?.phone
  );
  const canConfirmShareReport =
    !shareReportMobileError &&
    !whatsappSending &&
    !pdfLoading &&
    !pdfViewerError;

  const shareReportWhatsappLabel =
    lastReportFetchParams?.variant === "derivative"
      ? "Send Super ECG on WhatsApp"
      : "Send chart report on WhatsApp";

  const handleSendSuperEcgWhatsapp = async () => {
    if (
      !patient?.userId ||
      !lastReportFetchParams?.fileName ||
      whatsappSending ||
      shareReportMobileError
    ) {
      return;
    }

    setWhatsappSending(true);

    try {
      const response = await fetch(
        getSuperEcgWhatsappEndpoint(patient.userId),
        apiUtils.createRequestOptions("POST", {
          fileName: lastReportFetchParams.fileName,
          variant: lastReportFetchParams.variant ?? "original",
        })
      );
      await apiUtils.handleResponse(response);
      toast.success("Report is being sent on WhatsApp.");
      setShowShareReportModal(false);
    } catch {
      toast.error("We couldn't send the report on WhatsApp. Please try again.");
    } finally {
      setWhatsappSending(false);
    }
  };

  // Close PDF modal and cleanup
  const handleClosePdfModal = () => {
    reportFetchGenerationRef.current += 1;
    setShowPdfModal(false);
    setShowShareReportModal(false);
    setPdfLoading(false);
    setPdfViewerError(null);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  // Helper function to sort reports by date
  const getSortedReports = (): Array<
    Omit<Report, "createdAt"> & { reportType: string; createdAt: Date }
  > => {
    if (!reports) return [];

    const allReports: Array<
      Omit<Report, "createdAt"> & { reportType: string; createdAt: Date }
    > = [];

    Object.entries(reports).forEach(
      ([reportType, reportList]: [string, Report[]]) => {
        if (Array.isArray(reportList)) {
          reportList.forEach((report) => {
            allReports.push({
              ...report,
              reportType,
              createdAt: new Date(report.createdAt || report.uploadedAt || Date.now()),
            });
          });
        }
      }
    );

    return allReports.sort((a, b) => {
      if (reportSortOrder === "newest") {
        return b.createdAt.getTime() - a.createdAt.getTime();
      } else {
        return a.createdAt.getTime() - b.createdAt.getTime();
      }
    });
  };

  const isScrollableListTab = SCROLLABLE_LIST_TABS.has(selectedTab);

  return (
    <div className="flex min-h-[calc(100vh-72px)] flex-col overflow-x-hidden overflow-y-auto">
      <div className="flex flex-col gap-4 px-4 pt-4 pb-4">
        <div className="flex min-w-0 flex-col gap-4 xl:flex-row xl:items-stretch">
          <div className="flex min-w-0 flex-col gap-4 xl:min-h-[calc(100vh-88px)] xl:flex-1">
        {/* Patient header — left column on xl */}
        <div className="flex shrink-0 flex-col gap-3 rounded-2xl bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-[10px]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="break-words text-xl font-bold tracking-[-0.02em] text-guardian-body">
                {fullName}
              </span>
              {riskDisplay.label !== "-" && (
                <span
                  className={`rounded-md px-1.5 py-1 text-sm font-normal text-white ${riskDisplay.badgeClass}`}
                >
                  {riskDisplay.label}
                </span>
              )}
            </div>
            <DemographicRow>
              <span>{age} years</span>
              <DemographicSeparator />
              <span>{gender}</span>
              <DemographicSeparator />
              <span>{height}</span>
              <DemographicSeparator />
              <span>{bloodGroup}</span>
            </DemographicRow>
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-sm font-medium text-guardian-header">
                Contact:
              </span>
              <span className="text-sm font-bold text-guardian-body">{phone}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setShowEditPatientModal(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-guardian-10 transition-colors hover:bg-guardian-20"
              aria-label="Edit patient"
            >
              <HeaderEditIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingNoteId(null);
                setEditingNoteText("");
                setNewNote("");
                setShowNoteModal(true);
              }}
              className="flex h-10 items-center gap-2 rounded-full bg-guardian-10 px-3 text-sm font-normal text-[#02040A] transition-colors hover:bg-guardian-20"
            >
              <HeaderAddNoteIcon className="h-5 w-5" />
              <span>Add Note</span>
            </button>
          </div>
        </div>

        {/* AI Overview — stacked under header on mobile/tablet */}
        <div
          className={`shrink-0 overflow-hidden transition-[height,max-height] duration-200 ease-in-out xl:hidden ${
            isAiOverviewExpanded
              ? "h-[280px] max-h-[35vh] sm:h-[300px] lg:h-[320px] lg:max-h-[38vh]"
              : "h-12 max-h-12"
          }`}
        >
          <PatientAiOverviewPanel
            summaryHtml={aiSummary}
            loading={aiLoading}
            error={aiError}
            isExpanded={isAiOverviewExpanded}
            onToggleExpanded={() =>
              setIsAiOverviewExpanded((expanded) => !expanded)
            }
            contentId="patient-ai-overview-content"
            className="h-full"
          />
        </div>

          <div
            className={`flex min-w-0 w-full flex-col rounded-2xl bg-white max-lg:min-h-0 max-lg:flex-1 max-lg:overflow-hidden xl:min-w-0 xl:flex xl:w-auto xl:min-h-0 xl:flex-1 xl:flex-col ${
              isScrollableListTab
                ? "overflow-hidden"
                : "max-lg:overflow-hidden lg:overflow-visible"
            }`}
          >
            {/* Tab bar */}
            <div className="shrink-0 border-b border-guardian-20 p-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
                <div
                  ref={tabListRef}
                  className="scrollbar-hide flex min-w-0 gap-2 overflow-x-auto pb-1 lg:flex-1"
                >
                  {PATIENT_DETAIL_TABS.map((tabId) => {
                    const TabIcon = getPatientDetailTabIcon(tabId);
                    return (
                      <button
                        key={tabId}
                        ref={(el) => {
                          tabButtonRefs.current[tabId] = el;
                        }}
                        type="button"
                        className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors duration-150 focus:outline-none ${
                          selectedTab === tabId
                            ? "bg-guardian-10 text-guardian-body"
                            : "text-guardian-body hover:bg-guardian-10"
                        }`}
                        onClick={() => {
                          setIsAiOverviewExpanded(false);
                          setSelectedTab(tabId);
                        }}
                        onFocus={(event) => {
                          if (event.currentTarget.matches(":focus-visible")) {
                            setIsAiOverviewExpanded(false);
                          }
                        }}
                      >
                        <TabIcon className="h-5 w-5 shrink-0" />
                        <span>{tabId}</span>
                      </button>
                    );
                  })}
                </div>
                {selectedTab === "Health Metrics" && patient && (
                  <div className="flex w-full max-lg:order-2 gap-2 max-lg:w-full lg:ml-auto lg:w-auto lg:shrink-0 lg:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAiOverviewExpanded(false);
                        resetAddVitalForm();
                        setShowAddVitalModal(true);
                      }}
                      className="flex h-8 max-lg:w-full max-lg:flex-1 max-lg:justify-center items-center gap-2 rounded-full bg-rag-green px-2.5 text-xs font-semibold text-white transition-colors hover:opacity-90 focus:outline-none lg:shrink-0"
                    >
                      <AddVitalIcon className="h-5 w-5 shrink-0" />
                      Add vitals
                    </button>
                  </div>
                )}
                {selectedTab === "Reports" && patient && (
                  <div className="flex w-full max-lg:order-2 gap-2 max-lg:w-full lg:ml-auto lg:w-auto lg:shrink-0 lg:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsAiOverviewExpanded(false);
                        resetUploadReportForm();
                        setShowUploadReportModal(true);
                      }}
                      className="flex h-8 min-h-8 max-lg:w-full max-lg:flex-1 max-lg:justify-center items-center gap-1.5 rounded-full bg-rag-green px-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#0B7A72] focus:outline-none lg:shrink-0"
                    >
                      <UploadReportIcon className="h-5 w-5 shrink-0 text-white" />
                      <span className="hidden sm:inline">Upload reports</span>
                      <span className="sm:hidden">Upload</span>
                    </button>
                    <div className="relative max-lg:flex-1 max-lg:min-w-0 lg:shrink-0" ref={dropdownRef}>
                      <button
                        type="button"
                        className="flex h-8 min-h-8 w-full items-center justify-center gap-1 rounded-full bg-guardian-10 px-2.5 text-xs font-semibold text-[#02040A] transition-colors hover:bg-guardian-20"
                        onClick={() => {
                          setIsAiOverviewExpanded(false);
                          setShowSortDropdown(!showSortDropdown);
                        }}
                        aria-expanded={showSortDropdown}
                        aria-haspopup="listbox"
                      >
                        {reportSortOrder === "newest" ? "Newest" : "Oldest"}
                        <ChevronDown className="h-4 w-4 shrink-0 text-guardian-80" />
                      </button>
                      {showSortDropdown && (
                        <div
                          className={`${CSS_CLASSES.DROPDOWN_MENU.PANEL} ${CSS_CLASSES.DROPDOWN_MENU.PANEL_LEFT} md:left-auto md:right-0 md:w-48`}
                          role="listbox"
                        >
                          <button
                            type="button"
                            role="option"
                            aria-selected={reportSortOrder === "newest"}
                            className={`${CSS_CLASSES.DROPDOWN_MENU.ITEM} flex items-center justify-between gap-2 ${
                              reportSortOrder === "newest"
                                ? CSS_CLASSES.DROPDOWN_MENU.ITEM_SELECTED
                                : ""
                            }`}
                            onClick={() => {
                              setIsAiOverviewExpanded(false);
                              setReportSortOrder("newest");
                              setShowSortDropdown(false);
                            }}
                          >
                            Newest
                            {reportSortOrder === "newest" && (
                              <Check className="h-4 w-4 shrink-0" aria-hidden />
                            )}
                          </button>
                          <button
                            type="button"
                            role="option"
                            aria-selected={reportSortOrder === "oldest"}
                            className={`${CSS_CLASSES.DROPDOWN_MENU.ITEM} flex items-center justify-between gap-2 ${
                              reportSortOrder === "oldest"
                                ? CSS_CLASSES.DROPDOWN_MENU.ITEM_SELECTED
                                : ""
                            }`}
                            onClick={() => {
                              setIsAiOverviewExpanded(false);
                              setReportSortOrder("oldest");
                              setShowSortDropdown(false);
                            }}
                          >
                            Oldest
                            {reportSortOrder === "oldest" && (
                              <Check className="h-4 w-4 shrink-0" aria-hidden />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tab content */}
            <div
              className={`flex min-h-0 flex-col max-lg:min-h-0 max-lg:flex-1 xl:flex-1 xl:overflow-hidden ${
                selectedTab === "Chat"
                  ? "max-lg:overflow-y-auto p-0"
                  : isScrollableListTab
                    ? selectedTab === "Questionnaire"
                      ? "overflow-hidden p-3 max-xl:h-[min(28rem,calc(100dvh-18rem))] max-xl:min-h-[17.5rem] xl:min-h-0"
                      : "overflow-hidden p-3 max-xl:max-h-[min(28rem,calc(100dvh-18rem))] max-xl:min-h-[17.5rem] xl:min-h-0"
                    : "max-lg:overflow-y-auto overflow-y-auto p-3"
              }`}
            >
              {selectedTab === "Health Metrics" && (
                <HealthMetricsDashboard
                  vitals={patient.vitals}
                  heartRateDataForChart={heartRateDataForChart}
                  setHeartRateDataForChart={setHeartRateDataForChart}
                  bloodPressureDataForChart={bloodPressureDataForChart}
                  setBloodPressureDataForChart={setBloodPressureDataForChart}
                  bloodOxygenDataForChart={bloodOxygenDataForChart}
                  setBloodOxygenDataForChart={setBloodOxygenDataForChart}
                  heartRateTimeLabels={heartRateTimeLabels}
                  setHeartRateTimeLabels={setHeartRateTimeLabels}
                  bloodPressureTimeLabels={bloodPressureTimeLabels}
                  setBloodPressureTimeLabels={setBloodPressureTimeLabels}
                  bloodOxygenTimeLabels={bloodOxygenTimeLabels}
                  setBloodOxygenTimeLabels={setBloodOxygenTimeLabels}
                  latestHeartRate={latestHeartRate}
                  setLatestHeartRate={setLatestHeartRate}
                  latestBloodPressure={latestBloodPressure}
                  setLatestBloodPressure={setLatestBloodPressure}
                  latestBloodOxygen={latestBloodOxygen}
                  setLatestBloodOxygen={setLatestBloodOxygen}
                  latestHeartRateTime={latestHeartRateTime}
                  setLatestHeartRateTime={setLatestHeartRateTime}
                  latestBloodPressureTime={latestBloodPressureTime}
                  setLatestBloodPressureTime={setLatestBloodPressureTime}
                  latestBloodOxygenTime={latestBloodOxygenTime}
                  setLatestBloodOxygenTime={setLatestBloodOxygenTime}
                />
              )}
              {selectedTab === "Chat" && (
                <ChatWidget
                  patientId={patient.userId}
                  doctorId={auth.getUserId() || "clinician"}
                  onClose={() => {}}
                  embedded
                  fullHeight
                />
              )}
              {selectedTab === "Medical History" && patient && (
                <MedicalHistoryTab
                  patient={patient}
                  reports={reports}
                  reportAnalysis={reportAnalysis}
                  reportsLoading={reportsLoading}
                  onReportClick={handleReportClick}
                />
              )}
                {selectedTab === "Notes" && (
                  <div className="flex h-full min-h-0 flex-col">
                    <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                      {notes.length === 0 ? (
                        <div className="text-sm text-guardian-header">
                          No notes yet.
                        </div>
                      ) : (
                        notes.map((note) => (
                          <div
                            key={note.id}
                            className="group flex flex-col items-start gap-2.5 border-b border-guardian-10 pb-2.5 pt-2.5 first:pt-0 sm:flex-row sm:flex-wrap sm:items-start"
                          >
                            <div className="flex min-w-0 w-full flex-1 flex-col gap-0">
                              <div className="text-[12px] leading-4 tracking-[0.4px] text-guardian-header">
                                {formatNoteDateTime(note.updatedAt ?? note.date)}
                              </div>
                              <div className="whitespace-pre-line break-words text-[14px] leading-5 tracking-[-0.013em] text-guardian-80">
                                {note.text}
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2 self-end opacity-100 focus-within:opacity-100 sm:self-auto sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                              <button
                                type="button"
                                onClick={() =>
                                  note.id && handleEditNote(note.id, note.text)
                                }
                                className="rounded p-1 transition-colors hover:bg-guardian-10"
                                title="Edit note"
                              >
                                <NoteEditIcon className="h-5 w-5" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  note.id && handleDeleteNote(note.id)
                                }
                                className="rounded p-1 transition-colors hover:bg-guardian-10"
                                title="Delete note"
                              >
                                <NoteDeleteIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {selectedTab === "Questionnaire" && (
                  <QuestionnaireTab
                    patient={patient}
                    onQuestionnaireSubmitted={fetchAISummary}
                  />
                )}
                {selectedTab === "Reports" && (
                  <ReportsTab
                    sortedReports={reports ? getSortedReports() : []}
                    reportAnalysis={reportAnalysis}
                    reportsLoading={reportsLoading}
                    reportsError={reportsError}
                    reportActionError={reportActionError}
                    onReportClick={handleReportClick}
                    onFetchReportVariant={fetchReportVariant}
                  />
                )}
            </div>
          </div>
          </div>

          <div className="hidden shrink-0 flex-col xl:sticky xl:top-4 xl:flex xl:w-[30%] xl:max-w-[454px] xl:self-stretch">
            <PatientAiOverviewPanel
              summaryHtml={aiSummary}
              loading={aiLoading}
              error={aiError}
              isExpanded
              contentId="patient-ai-overview-desktop-content"
              className="flex-1"
              contentOverflow="visible"
            />
          </div>
        </div>
      </div>

      {patient && (
        <PatientDetailModal
          isOpen={showEditPatientModal}
          onClose={handleCloseEditPatientModal}
          title="Edit Patient"
          subtitle="Update patient information."
          size="lg"
          overlayClassName={showPdfModal ? "!z-[60]" : undefined}
        >
          <PatientForm
            mode="edit"
            initialPatient={patient}
            onSubmit={handleEditPatient}
            onCancel={handleCloseEditPatientModal}
            submitLabel="Update Patient"
          />
        </PatientDetailModal>
      )}

      <PatientDetailModal
        isOpen={showAddVitalModal}
        onClose={() => {
          setShowAddVitalModal(false);
          resetAddVitalForm();
        }}
        title="Add vital record"
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setShowAddVitalModal(false);
                resetAddVitalForm();
              }}
              className={CSS_CLASSES.BUTTONS.CANCEL_MODAL}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddVitalSubmit}
              disabled={addVitalSubmitting || !vitalConfig}
              className={CSS_CLASSES.BUTTONS.PRIMARY_MODAL}
            >
              {addVitalSubmitting ? "Saving…" : "Save vitals"}
            </button>
          </>
        }
      >
        <div className="space-y-4 p-4 sm:p-6">
          {addVitalErrors.form && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-rag-red">
              {addVitalErrors.form}
            </div>
          )}

          <div>
            <label htmlFor="add-vital-hr" className="mb-1 block text-sm font-medium text-guardian-header">
              Heart Rate (bpm)
            </label>
            <input
              id="add-vital-hr"
              type="number"
              min={HEART_RATE_MIN}
              max={HEART_RATE_MAX}
              placeholder="e.g. 72"
              value={addVitalHeartRate}
              onChange={(e) => setAddVitalHeartRate(e.target.value)}
              className={CSS_CLASSES.INPUTS.PATIENT}
            />
            {addVitalErrors.heartRate && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{addVitalErrors.heartRate}</p>
            )}
          </div>

          <div>
            <label htmlFor="add-vital-bp-group" className="mb-1 block text-sm font-medium text-guardian-header">
              Blood Pressure (mmHg)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="add-vital-systolic" className="mb-1 block text-sm font-medium text-guardian-header">
                  Systolic
                </label>
                <input
                  id="add-vital-systolic"
                  type="number"
                  min={BP_SYSTOLIC_MIN}
                  max={BP_SYSTOLIC_MAX}
                  placeholder="e.g. 120"
                  value={addVitalSystolic}
                  onChange={(e) => setAddVitalSystolic(e.target.value)}
                  className={CSS_CLASSES.INPUTS.PATIENT}
                />
                {addVitalErrors.systolic && (
                  <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{addVitalErrors.systolic}</p>
                )}
              </div>
              <div>
                <label htmlFor="add-vital-diastolic" className="mb-1 block text-sm font-medium text-guardian-header">
                  Diastolic
                </label>
                <input
                  id="add-vital-diastolic"
                  type="number"
                  min={BP_DIASTOLIC_MIN}
                  max={BP_DIASTOLIC_MAX}
                  placeholder="e.g. 80"
                  value={addVitalDiastolic}
                  onChange={(e) => setAddVitalDiastolic(e.target.value)}
                  className={CSS_CLASSES.INPUTS.PATIENT}
                />
                {addVitalErrors.diastolic && (
                  <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{addVitalErrors.diastolic}</p>
                )}
              </div>
            </div>
            {addVitalErrors.bloodPressure && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{addVitalErrors.bloodPressure}</p>
            )}
          </div>

          <div>
            <label htmlFor="add-vital-oxygen" className="mb-1 block text-sm font-medium text-guardian-header">
              Oxygen Saturation (%)
            </label>
            <input
              id="add-vital-oxygen"
              type="number"
              min={OXYGEN_MIN}
              max={OXYGEN_MAX}
              step={0.1}
              placeholder="e.g. 98"
              value={addVitalOxygen}
              onChange={(e) => setAddVitalOxygen(e.target.value)}
              className={CSS_CLASSES.INPUTS.PATIENT}
            />
            {addVitalErrors.oxygen && (
              <p className={`mt-1 ${CSS_CLASSES.TEXT.ERROR}`}>{addVitalErrors.oxygen}</p>
            )}
          </div>
        </div>
      </PatientDetailModal>

      <PatientDetailModal
        isOpen={showNoteModal}
        onClose={() => {
          setShowNoteModal(false);
          setEditingNoteId(null);
          setEditingNoteText("");
          setNewNote("");
        }}
        title={editingNoteId ? "Edit Note" : "Add Note"}
        size="md"
        footer={
          <>
            <button
              type="button"
              className={CSS_CLASSES.BUTTONS.CANCEL_MODAL}
              onClick={() => {
                setShowNoteModal(false);
                setEditingNoteId(null);
                setEditingNoteText("");
                setNewNote("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={CSS_CLASSES.BUTTONS.PRIMARY_MODAL}
              onClick={handleSaveNote}
            >
              {editingNoteId ? "Update" : "Save"}
            </button>
          </>
        }
      >
        <div className="p-4 sm:p-6">
          <textarea
            className={CSS_CLASSES.INPUTS.TEXTAREA}
            rows={4}
            value={editingNoteId ? editingNoteText : newNote}
            onChange={(e) =>
              editingNoteId
                ? setEditingNoteText(e.target.value)
                : setNewNote(e.target.value)
            }
            placeholder="Enter your note here..."
          />
        </div>
      </PatientDetailModal>

      <PatientDetailModal
        isOpen={!!noteToDelete}
        onClose={() => setNoteToDelete(null)}
        title="Delete Note"
        size="sm"
        footer={
          <>
            <button
              type="button"
              className={CSS_CLASSES.BUTTONS.CANCEL_MODAL}
              onClick={() => setNoteToDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={CSS_CLASSES.BUTTONS.DANGER_MODAL}
              onClick={confirmDeleteNote}
            >
              Delete
            </button>
          </>
        }
      >
        <p className="p-4 text-sm text-guardian-body sm:px-6 sm:pb-6">
          Are you sure you want to delete this note? This action cannot be undone.
        </p>
      </PatientDetailModal>

      <PatientDetailModal
        isOpen={showUploadReportModal}
        onClose={() => {
          setShowUploadReportModal(false);
          resetUploadReportForm();
        }}
        title="Upload report"
        subtitle="Analysis runs after upload."
        size="md"
        closeDisabled={uploadReportSubmitting}
        footer={
          <>
            <button
              type="button"
              className={CSS_CLASSES.BUTTONS.CANCEL_MODAL}
              disabled={uploadReportSubmitting}
              onClick={() => {
                setShowUploadReportModal(false);
                resetUploadReportForm();
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={CSS_CLASSES.BUTTONS.PRIMARY_MODAL}
              disabled={uploadReportSubmitting}
              onClick={handleUploadReportSubmit}
            >
              {uploadReportSubmitting ? "Uploading…" : "Upload"}
            </button>
          </>
        }
      >
        <div className="space-y-4 p-4 sm:p-6">
          <div>
            <label
              htmlFor="upload-report-type"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              Report type
            </label>
            <select
              id="upload-report-type"
              className={CSS_CLASSES.INPUTS.PATIENT}
              value={uploadReportType}
              onChange={(e) => setUploadReportType(e.target.value)}
            >
              <option value="">Select type</option>
              {CLINICIAN_REPORT_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="upload-report-file"
              className="mb-1 block text-sm font-medium text-guardian-header"
            >
              File
            </label>
            <input
              id="upload-report-file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              className={CSS_CLASSES.INPUTS.FILE}
              onChange={(e) => {
                const f = e.target.files?.[0];
                setUploadReportFile(f ?? null);
                setUploadReportError(null);
              }}
            />
            {uploadReportFile && (
              <p className={`mt-1 truncate ${CSS_CLASSES.TEXT.MUTED}`}>
                Selected: {uploadReportFile.name}
              </p>
            )}
            <p className={`mt-2 ${CSS_CLASSES.TEXT.MUTED}`}>
              PDF, JPG, JPEG, or PNG (max 5 MB).
            </p>
          </div>
          {uploadReportError && (
            <p className={CSS_CLASSES.TEXT.ERROR}>{uploadReportError}</p>
          )}
        </div>
      </PatientDetailModal>
      {/* PDF Viewer Modal */}
      {showPdfModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl h-[92vh] sm:h-[95vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between gap-3">
                <h2 className="min-w-0 flex-1 truncate text-lg font-bold text-gray-800">
                  Report Viewer
                </h2>
                <div className="flex shrink-0 items-center justify-end gap-2">
                  {canShareChartReportOnWhatsApp && (
                    <button
                      type="button"
                      onClick={() => setShowShareReportModal(true)}
                      disabled={pdfLoading || Boolean(pdfViewerError)}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-700 transition-colors hover:bg-green-100 disabled:cursor-not-allowed disabled:bg-guardian-10 disabled:text-guardian-header"
                      aria-label={shareReportWhatsappLabel}
                      title={shareReportWhatsappLabel}
                    >
                      <WhatsAppIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={handleClosePdfModal}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full text-2xl font-bold text-gray-400 transition-colors hover:text-gray-600"
                    aria-label="Close report viewer"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 p-2 sm:p-4 min-h-0">
              {pdfLoading ? (
                <PageLoading
                  message={MESSAGES.LOADING.REPORT}
                  className="h-full"
                />
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0"
                  title="PDF Viewer"
                />
              ) : pdfViewerError ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 px-4 text-center">
                  <p className="text-sm text-rag-red">{pdfViewerError}</p>
                  {lastReportFetchParams && (
                    <button
                      type="button"
                      className="rounded-lg bg-guardian-10 px-4 py-2 text-sm font-medium text-guardian-body transition-colors hover:bg-guardian-20"
                      onClick={() =>
                        fetchReportVariant({
                          ...lastReportFetchParams,
                          mode: "view",
                        })
                      }
                    >
                      Retry
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-600">No report to display.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <PatientDetailModal
        isOpen={showShareReportModal}
        onClose={() => setShowShareReportModal(false)}
        title="Report Sharing"
        size="sm"
        closeDisabled={whatsappSending}
        overlayClassName="!z-[60]"
        footer={
          <>
            <button
              type="button"
              className={CSS_CLASSES.BUTTONS.CANCEL_MODAL}
              disabled={whatsappSending}
              onClick={() => setShowShareReportModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={CSS_CLASSES.BUTTONS.PRIMARY_MODAL}
              disabled={!canConfirmShareReport}
              onClick={handleSendSuperEcgWhatsapp}
            >
              {whatsappSending ? "Sharing…" : "Share Report"}
            </button>
          </>
        }
      >
        <div className="p-4 sm:px-6 sm:pb-6">
          <p className="text-sm text-guardian-body">
            Please confirm the patient&apos;s mobile number:
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 break-all text-sm font-bold text-guardian-body">
              {patientPhoneDisplay}
            </span>
            <button
              type="button"
              onClick={handleEditPatientFromShareModal}
              disabled={whatsappSending}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-guardian-10 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Edit patient mobile number"
            >
              <HeaderEditIcon className="h-5 w-5" />
            </button>
          </div>
          {shareReportMobileError && (
            <p className={`mt-2 ${CSS_CLASSES.TEXT.ERROR}`}>
              {shareReportMobileError}
            </p>
          )}
        </div>
      </PatientDetailModal>
    </div>
  );
}
